import React from "react";
import type { ProfileData, ProfilerNodeTiming } from "@recursive-robot/react-jsx-parser";
import { nodeLabel } from "../../../store/profiling/selectors";
import { formatMs } from "./viz";
import CodeBlock from "../CodeBlock";

type Props = {
    batch: ProfileData;
    node: ProfilerNodeTiming | null;
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="node-field">
            <span className="nf-label">{label}</span>
            <span className="nf-value">{value}</span>
        </div>
    );
}

function NodeDetail({ batch, node }: Props) {
    if (!node) {
        return <div className="node-detail empty">Select a frame to inspect its timing and source.</div>;
    }

    const relStart = node.startTime - batch.startTime;

    return (
        <div className="node-detail">
            <div className="node-detail-title">{nodeLabel(node)}</div>

            <div className="node-fields">
                <Field label="Node type" value={node.nodeType} />
                <Field label="Self time" value={formatMs(node.selfTime)} />
                <Field label="Total time" value={formatMs(node.totalTime)} />
                <Field label="Start (rel.)" value={`+${formatMs(relStart)}`} />
                <Field label="Depth" value={node.depth} />
                {node.loopIndex != null && <Field label="Loop index" value={node.loopIndex} />}
                <Field label="Location" value={`${node.location.line}:${node.location.column ?? 0}`} />
                {node.phase && <Field label="React phase" value={node.phase} />}
                {node.baseDuration != null && <Field label="Base duration" value={formatMs(node.baseDuration)} />}
            </div>

            <div className="node-source-caption">Source</div>
            {/* node.source is the exact text of this AST node (offsets are relative to the trimmed
                template, so we render the node's own captured source rather than re-slicing). */}
            <CodeBlock maxHeight={null} code={node.source} />
        </div>
    );
}

export default React.memo(NodeDetail);
